'use client'

import Link from 'next/link'
import { FC, ReactNode } from 'react'
import { isEmail, isNonClickable, isPhone, takeAnother } from '@/root/modules/tuuleliinid/AppLayout'
import { ContentContainer } from '@/root/components/shared'
import { MenuItem } from '@/root/shared-types'
import { useAuth, useCustomRouter, useGetAuthToken, useGetTranslations } from '@/root/shared-hooks'
import { APP_ROUTES, testIds } from '@/root/shared-consts'
import { UKFlagIcon } from '@/root/ui/shared/icons/UKFlagIcon'
import { EstoniaFlagIcon } from '@/root/ui/shared/icons/EstoniaFlagIcon'
import { MailIcon } from '@/root/ui/shared/icons/MailIcon'
import { PhoneIcon } from '@/root/ui/shared/icons/PhoneIcon'
import { QuestionIcon } from '@/root/ui/shared/icons/QuestionIcon'
import { UserIcon } from '@/root/ui/shared/icons/UserIcon'

interface TopHeaderMenuProps {
  menuItems: MenuItem[]
}

const iconClassName = 'w-5 h-5 inline-block mr-1 flex-shrink-0'

const PROFILE_LINK = '/auth/signup'

const FLAG_ICONS: Record<string, ReactNode> = {
  en: <UKFlagIcon className="inline-block w-5" />,
  et: <EstoniaFlagIcon className="inline-block w-5" />,
}

const ICON_MAP: Record<string, ReactNode> = {
  '/content/help': <QuestionIcon className={iconClassName} />,
  [PROFILE_LINK]: <UserIcon className={iconClassName} />,
  phone: <PhoneIcon className={iconClassName} />,
  email: <MailIcon className={iconClassName} />,
}

const getMenuIcon = (link: string, subject: string) => {
  if (isPhone(subject)) return ICON_MAP.phone
  if (isEmail(subject)) return ICON_MAP.email
  return ICON_MAP[link] || null
}

const TopHeaderMenu: FC<TopHeaderMenuProps> = ({ menuItems }) => {
  const { locale, locales, getPathWithLocale } = useCustomRouter()
  const { isAuth } = useGetAuthToken()
  const { logOut } = useAuth()
  const { getTranslation } = useGetTranslations()

  const otherLocale = takeAnother(locales, locale)

  return (
    <div data-testid={testIds.header.upperBar} className="bg-primary-base hidden h-10 md:block">
      <ContentContainer className="h-full">
        <ul className="text-base-lightest flex h-full w-full items-center justify-between gap-2 px-4 py-2 text-sm font-semibold lg:gap-32 lg:text-base">
          {menuItems.map(({ id, link, subject }) => {
            const icon = getMenuIcon(link, subject)

            if (link === PROFILE_LINK) {
              return (
                <li key={id} className="flex items-center gap-4 whitespace-nowrap">
                  {isAuth ? (
                    <>
                      <Link
                        href={`/${locale}${APP_ROUTES.details}`}
                        data-testid={testIds.header.navProfileSettings}
                        className="darken flex items-center gap-1"
                      >
                        {icon}
                        <span className="leading-none">{getTranslation('profilePageTitle')}</span>
                      </Link>
                      <button
                        onClick={logOut}
                        data-testid={testIds.header.navLogout}
                        className="darken flex items-center gap-1"
                      >
                        <span className="leading-none">{getTranslation('headerLogoutButtonLabel')}</span>
                      </button>
                    </>
                  ) : (
                    <Link href={`/${locale}${APP_ROUTES.signup}`} className="darken flex items-center gap-1">
                      {icon}
                      <span className="leading-none">{subject}</span>
                    </Link>
                  )}
                </li>
              )
            }

            if (isNonClickable(subject)) {
              return (
                <li key={id} className="flex cursor-default items-center gap-1 whitespace-nowrap">
                  {icon}
                  <span className="leading-none">{subject}</span>
                </li>
              )
            }

            return (
              <li key={id} className="darken flex items-center gap-1 whitespace-nowrap">
                <Link href={`/${locale}${link}`} className="flex items-center gap-1">
                  {icon}
                  <span className="leading-none">{subject}</span>
                </Link>
              </li>
            )
          })}
          <li className="darken flex shrink-0 items-center justify-self-end">
            <Link href={getPathWithLocale(otherLocale)} className="flex cursor-pointer items-center">
              {FLAG_ICONS[otherLocale] || null}
            </Link>
          </li>
        </ul>
      </ContentContainer>
    </div>
  )
}

export default TopHeaderMenu
